import type { BankAccountPayoutsBank } from "@src/backend/src/common/data/banks";
import type { ERP } from "@src/backend/src/rentals/data/erps";
import type { OnboardingCity } from "@src/backend/src/rentals/data/onboarding";
import type { DocumentKey } from "./constants";
import type { FormData } from "./types";

export const createInitialData = (): FormData => ({
  tipoPersona: "juridica",
  nombre: "",
  nombreComercial: "",
  identificacion: "",
  representanteLegalNombre: "",
  representanteLegalIdentificacion: "",
  direccion: "",
  ciudad: "",
  otraCiudad: "",
  ciiu: "",
  web: "",
  celular: "",
  correoCliente: "",
  erp: "",
  otroErp: "",
  contratos: "",
  cartera: "",
  modeloRecaudo: "agregador",
  codigoPse: "",
  banco: "",
  tipoCuenta: "",
  numeroCuenta: "",
  titularCuentaEsComercio: true,
  titularCuentaNombre: "",
  titularCuentaTipoDocumento: "",
  titularCuentaNumeroDocumento: "",
  palommaPlus: true,
  correoFacturacion: "",
  whatsappFacturacion: "",
  pep: "no",
  cripto: "no",
  pepNombre: "",
  pepCargo: "",
  pepDetalle: "",
  criptoActividad: "",
  criptoRelacion: "",
  criptoDetalle: "",
  origenFondos: false,
  tratamientoDatos: false,
  aceptaContrato: false,
});

const digitsOnly = (value: string): string => value.replace(/\D/g, "");

/** Flat payload for draft/submit matching the merchant-aligned API */
export const getDraftPayload = (data: FormData) => ({
  displayName: data.nombreComercial.trim() || undefined,
  supportEmail: data.correoCliente.trim() || undefined,
  supportWhatsapp: data.celular.trim() || undefined,
  erp: (data.erp || undefined) as ERP | undefined,
  otherErp: data.erp === "none" ? data.otroErp.trim() : undefined,
  website: data.web.trim() || undefined,
  gateway: data.modeloRecaudo === "gateway",
  ciiu: data.ciiu.trim() || undefined,
  contractCount: data.contratos ? Number(data.contratos) : undefined,
  portfolioCop: digitsOnly(data.cartera)
    ? Number(digitsOnly(data.cartera))
    : undefined,
  pseServiceCode: data.codigoPse.trim() || undefined,
  palommaPlusEnabled: data.palommaPlus,
  billingInfo: {
    documentType: (data.tipoPersona === "juridica" ? "nit" : "cc") as
      | "nit"
      | "cc",
    documentNumber: data.identificacion.trim(),
    name: data.nombre.trim(),
    email: data.correoFacturacion.trim() || undefined,
    phoneNumber: data.whatsappFacturacion.trim() || undefined,
    city: data.ciudad as OnboardingCity,
    otherCity: data.ciudad === "other" ? data.otraCiudad.trim() : undefined,
    address: data.direccion.trim(),
    // Retención is independent of document type; backend preserves an existing
    // value and only defaults from document type when unset.
  },
  legalRepresentative:
    data.tipoPersona === "juridica"
      ? {
          name: data.representanteLegalNombre.trim(),
          documentNumber: data.representanteLegalIdentificacion.trim(),
          documentType: "cc" as const,
        }
      : undefined,
  merchantAccount: data.banco
    ? {
        bank: data.banco as BankAccountPayoutsBank,
        accountType: data.tipoCuenta as "savings" | "checking",
        accountNumber: data.numeroCuenta.trim(),
        holderIsCompany: data.titularCuentaEsComercio,
        holder: data.titularCuentaEsComercio
          ? undefined
          : {
              name: data.titularCuentaNombre.trim(),
              documentType: data.titularCuentaTipoDocumento as "nit" | "cc",
              documentNumber: data.titularCuentaNumeroDocumento.trim(),
            },
      }
    : undefined,
  pep: data.pep === "si",
  pepDetails:
    data.pep === "si"
      ? {
          name: data.pepNombre.trim(),
          role: data.pepCargo.trim(),
          notes: data.pepDetalle.trim(),
        }
      : undefined,
  cryptoRelated: data.cripto === "si",
  cryptoDetails:
    data.cripto === "si"
      ? {
          activity: data.criptoActividad.trim(),
          relationship: data.criptoRelacion.trim(),
          notes: data.criptoDetalle.trim(),
        }
      : undefined,
  acceptedSourceOfFunds: data.origenFondos,
  acceptedDataPolicy: data.tratamientoDatos,
  acceptedContract: data.aceptaContrato,
});

const isValidWebsite = (value: string): boolean => {
  try {
    const url = new URL(value);
    return (
      (url.protocol === "http:" || url.protocol === "https:") &&
      url.hostname.includes(".")
    );
  } catch {
    return false;
  }
};

export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const isValidPhoneNumber = (phone: string): boolean => {
  const phoneRegex = /^(3\d{9})$/;
  return phoneRegex.test(phone);
};

const isValidNit = (nit: string): boolean => /^\d{10}$/.test(nit);

const isValidCedula = (cedula: string): boolean => /^\d+$/.test(cedula);

export const validateStep = (
  step: number,
  data: FormData,
  uploadedDocs: Partial<Record<DocumentKey, string>>
): Record<string, string> => {
  const errors: Record<string, string> = {};

  if (step === 1) {
    if (!data.nombreComercial.trim())
      errors.nombreComercial = "Campo requerido";
    if (!data.erp) errors.erp = "Campo requerido";
    if (data.erp === "none" && !data.otroErp.trim())
      errors.otroErp = "Campo requerido";
    if (
      !Number.isFinite(Number(data.contratos)) ||
      Number(data.contratos) <= 0
    ) {
      errors.contratos = "Ingresa una cantidad mayor a cero";
    }
    if (Number(digitsOnly(data.cartera)) <= 0) {
      errors.cartera = "Ingresa un valor mayor a cero";
    }
  }

  if (step === 2) {
    if (!data.nombre.trim()) errors.nombre = "Campo requerido";
    if (!data.identificacion.trim()) errors.identificacion = "Campo requerido";
    if (!data.ciudad) errors.ciudad = "Campo requerido";
    if (data.ciudad === "other" && !data.otraCiudad.trim())
      errors.otraCiudad = "Campo requerido";
    if (!data.direccion.trim()) errors.direccion = "Campo requerido";
    if (
      data.identificacion.trim() &&
      data.tipoPersona === "juridica" &&
      !isValidNit(data.identificacion.trim())
    )
      errors.identificacion = "NIT inválido";
    if (
      data.identificacion.trim() &&
      data.tipoPersona === "natural" &&
      !isValidCedula(data.identificacion.trim())
    )
      errors.identificacion = "Cédula inválida";
    if (!data.ciiu.trim()) errors.ciiu = "Campo requerido";
    if (!data.web.trim())
      errors.web =
        "Campo requerido. También puedes usar el enlace de una red social.";
    else if (!isValidWebsite(data.web.trim()))
      errors.web =
        "URL inválida. Debe incluir https:// (sitio web o red social).";
    if (data.tipoPersona === "juridica") {
      if (!data.representanteLegalNombre.trim())
        errors.representanteLegalNombre = "Campo requerido";
      if (!data.representanteLegalIdentificacion.trim())
        errors.representanteLegalIdentificacion = "Campo requerido";
      else if (!isValidCedula(data.representanteLegalIdentificacion.trim()))
        errors.representanteLegalIdentificacion = "Cédula inválida";
    }
  }

  if (step === 3) {
    if (!data.banco) errors.banco = "Campo requerido";
    if (!data.tipoCuenta) errors.tipoCuenta = "Campo requerido";
    if (!data.numeroCuenta.trim()) errors.numeroCuenta = "Campo requerido";
    if (!data.titularCuentaEsComercio) {
      if (!data.titularCuentaNombre.trim())
        errors.titularCuentaNombre = "Campo requerido";
      if (!data.titularCuentaTipoDocumento)
        errors.titularCuentaTipoDocumento = "Campo requerido";
      if (!data.titularCuentaNumeroDocumento.trim())
        errors.titularCuentaNumeroDocumento = "Campo requerido";
      else if (
        data.titularCuentaTipoDocumento === "cc" &&
        !isValidCedula(data.titularCuentaNumeroDocumento.trim())
      )
        errors.titularCuentaNumeroDocumento = "Cédula inválida";
      else if (
        data.titularCuentaTipoDocumento === "nit" &&
        !isValidNit(data.titularCuentaNumeroDocumento.trim())
      )
        errors.titularCuentaNumeroDocumento = "NIT inválido";
    }
  }

  if (step === 4) {
    if (!uploadedDocs.logo) errors.logo = "Debes cargar tu logo";
    if (!data.celular.trim()) errors.celular = "Campo requerido";
    else if (!isValidPhoneNumber(data.celular.trim()))
      errors.celular = "Número de celular inválido";
    if (!data.correoCliente.trim()) errors.correoCliente = "Campo requerido";
    else if (!isValidEmail(data.correoCliente.trim()))
      errors.correoCliente = "Correo inválido";
  }

  if (step === 5) {
    if (!data.correoFacturacion.trim())
      errors.correoFacturacion = "Campo requerido";
    if (!data.whatsappFacturacion.trim())
      errors.whatsappFacturacion = "Campo requerido";
    if (
      data.correoFacturacion.trim() &&
      !isValidEmail(data.correoFacturacion.trim())
    )
      errors.correoFacturacion = "Correo inválido";
    if (
      data.whatsappFacturacion.trim() &&
      !isValidPhoneNumber(data.whatsappFacturacion.trim())
    )
      errors.whatsappFacturacion = "Número de celular inválido";
  }

  if (step === 6) {
    if (!data.origenFondos) errors.origenFondos = "Campo requerido";
    if (!data.tratamientoDatos) errors.tratamientoDatos = "Campo requerido";
    if (!data.aceptaContrato) errors.aceptaContrato = "Campo requerido";
    if (data.pep === "si") {
      if (!data.pepNombre.trim()) errors.pepNombre = "Campo requerido";
      if (!data.pepCargo.trim()) errors.pepCargo = "Campo requerido";
      if (!data.pepDetalle.trim()) errors.pepDetalle = "Campo requerido";
    }
    if (data.cripto === "si") {
      if (!data.criptoActividad.trim())
        errors.criptoActividad = "Campo requerido";
      if (!data.criptoRelacion.trim())
        errors.criptoRelacion = "Campo requerido";
      if (!data.criptoDetalle.trim()) errors.criptoDetalle = "Campo requerido";
    }
    // Logo is uploaded and validated in step 4; only step-6 cards here.
    for (const key of [
      "rut",
      "certificacion_bancaria",
      "camara_comercio",
    ] as const) {
      if (!uploadedDocs[key]) errors[key] = "Debes cargar este documento";
    }
  }

  return errors;
};
